import { UserDetail } from '@copilot/common/pages/accountDetailsPage/types';
import notificationManager from '@copilot/common/utils/notificationManager';
import { InfoCircleFilled } from '@ant-design/icons';
import { Typography } from 'antd';
import {
	useActivateUsersMutation,
	useDeactivateUsersMutation,
} from '@copilot/data/graphql/_generated';
import { HookAPI } from 'antd/lib/modal/useModal';

/**
 * Hook that encapsulates functions for activating/deactivating users.
 * @param modal
 */
export function useUserActivateUpdate(modal: HookAPI) {
	const [activateUser, { loading: activateLoading }] = useActivateUsersMutation({
		refetchQueries: ['account'],
	});
	const [deactivateUser, { loading: deactivateLoading }] = useDeactivateUsersMutation({
		refetchQueries: ['account'],
	});

	function onUserActivate(user: UserDetail) {
		activateUser({
			variables: {
				input: {
					accountId: user.accountId,
					users: [user.id],
				},
			},
		})
			.then(() =>
				notificationManager.showSuccessNotification({
					message: 'User successfully reactivated',
				})
			)
			.catch((error) =>
				notificationManager.showErrorNotification({
					message: 'Update failed',
					description: error.message,
				})
			);
	}
	function onUserDeactivate(user: UserDetail) {
		if (user.isPrimaryContact) {
			modal.confirm({
				okText: 'Got it!',
				title: 'Action required: changing primary contacts',
				cancelText: 'Cancel',
				onCancel: () => {},
				icon: <InfoCircleFilled />,
				width: 600,
				content: (
					<Typography.Paragraph>
						At this time, primary contacts cannot be deactivated directly. Please
						contact our support team to change the primary contact before deactivating
						this user
					</Typography.Paragraph>
				),
				onOk: async () => {},
			});
			return;
		}
		deactivateUser({
			variables: {
				input: {
					accountId: user.accountId,
					users: [user.id],
				},
			},
		})
			.then(() =>
				notificationManager.showSuccessNotification({
					message: 'User successfully deactivated',
				})
			)
			.catch((error) =>
				notificationManager.showErrorNotification({
					message: 'Update failed',
					description: error.message,
				})
			);
	}

	return {
		onActivate: onUserActivate,
		onDeactivate: onUserDeactivate,
		isLoading: activateLoading || deactivateLoading,
	};
}
